/**
 * Time Trials Utility Functions
 * 
 * Helper functions for time trial formatting, parsing, and validation
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
 */

/**
 * Format time in seconds to MM:SS format
 * 
 * @param seconds - Time in seconds
 * @returns Formatted time string (MM:SS)
 * 
 * Examples:
 * - formatTimeTrialTime(765) => "12:45"
 * - formatTimeTrialTime(59) => "0:59"
 * - formatTimeTrialTime(3661) => "61:01"
 */
export function formatTimeTrialTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Parse time input from MM:SS format to seconds
 * 
 * @param timeString - Time string in MM:SS format
 * @returns Time in seconds, or null if invalid
 * 
 * Examples:
 * - parseTimeInput("12:45") => 765
 * - parseTimeInput("0:59") => 59
 * - parseTimeInput("invalid") => null
 */
export function parseTimeInput(timeString: string): number | null {
  const parts = timeString.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);

  if (isNaN(minutes) || isNaN(seconds) || minutes < 0 || seconds < 0 || seconds > 59) {
    return null;
  }

  return minutes * 60 + seconds;
}

/**
 * Parse time input from separate minutes and seconds strings
 * 
 * @param minutes - Minutes string
 * @param seconds - Seconds string
 * @returns Time in seconds, or null if invalid
 */
export function parseTimeInputSeparate(minutes: string, seconds: string): number | null {
  const min = parseInt(minutes, 10);
  const sec = parseInt(seconds, 10);

  if (isNaN(min) || isNaN(sec) || min < 0 || sec < 0 || sec > 59) {
    return null;
  }

  return min * 60 + sec;
}

/**
 * Validation errors for time trial form
 */
export interface TimeTrialValidationErrors {
  rider_id?: string;
  minutes?: string;
  seconds?: string;
  trail_name?: string;
  direction?: string;
  notes?: string;
}

/**
 * Time trial form data
 */
export interface TimeTrialFormData {
  rider_id: string;
  minutes: string;
  seconds: string;
  trail_name: string;
  direction: string;
  notes: string;
}

/**
 * Validate time trial form data
 * 
 * @param data - Form data to validate
 * @returns Validation errors object (empty if valid)
 * 
 * Requirements: 9.2, 9.4, 9.5
 */
export function validateTimeTrialForm(data: TimeTrialFormData): TimeTrialValidationErrors {
  const errors: TimeTrialValidationErrors = {};

  // Validate rider_id
  if (!data.rider_id || data.rider_id.trim().length === 0) {
    errors.rider_id = 'Please select a rider';
  }

  // Validate minutes
  if (!data.minutes || data.minutes.trim().length === 0) {
    errors.minutes = 'Minutes are required';
  } else {
    const min = parseInt(data.minutes, 10);
    if (isNaN(min) || min < 0) {
      errors.minutes = 'Please enter valid minutes (0 or greater)';
    }
  }

  // Validate seconds
  if (!data.seconds || data.seconds.trim().length === 0) {
    errors.seconds = 'Seconds are required';
  } else {
    const sec = parseInt(data.seconds, 10);
    if (isNaN(sec)) {
      errors.seconds = 'Please enter valid seconds';
    } else if (sec < 0 || sec > 59) {
      errors.seconds = 'Seconds must be between 0 and 59';
    }
  }

  // Validate trail_name
  if (!data.trail_name || data.trail_name.trim().length === 0) {
    errors.trail_name = 'Trail name is required';
  } else if (data.trail_name.length > 100) {
    errors.trail_name = 'Trail name must be 100 characters or less';
  }

  // Validate direction
  if (!data.direction || data.direction.trim().length === 0) {
    errors.direction = 'Please select a direction';
  }

  // Validate notes (optional, but check length if provided)
  if (data.notes && data.notes.length > 500) {
    errors.notes = 'Notes must be 500 characters or less';
  }

  return errors;
}

/**
 * Check if validation errors object has any errors
 * 
 * @param errors - Validation errors object
 * @returns True if there are any errors
 */
export function hasValidationErrors(errors: TimeTrialValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Direction options for time trials
 */
export const DIRECTION_OPTIONS = [
  'Clockwise',
  'Counter-clockwise',
  'Out and Back',
  'Point to Point',
  'Other'
] as const;

export type Direction = typeof DIRECTION_OPTIONS[number];
